import React from "react";
import RichText from "../modules/RichText";
import { PortableTextBlock } from "@portabletext/types";
import { PortableTextComponentProps } from "next-sanity";
import { Box } from "@/components/atoms/Box";
import { Heading } from "@/components/atoms/Heading";

type PortableNumberedBlockValue = {
  title: string;
  content: PortableTextBlock[];
  resetCounter?: boolean;
};

/**
 * PortableNumberedBlock Component
 *
 * Renders a list item with a circular counter.
 * Uses CSS counters for automatic numbering, with a reset option.
 *
 * **Circular dependency:** This component imports RichText, which in turn registers
 * PortableNumberedBlock via a dynamic import (`next/dynamic`) to break the cycle at
 * runtime. This is a known architectural pattern, not a bug.
 *
 * @see docs/design-system-audit/03-dependency-analysis.md
 */
const PortableNumberedBlock = ({
  value,
}: PortableTextComponentProps<PortableNumberedBlockValue>) => {
  const { title, content, resetCounter } = value;
  const counterIncrementStyle = { counterIncrement: "numbered-block" } as const;

  return (
    <Box
      className={`not-prose numbered-block flex gap-6 md:gap-8 mb-12 group/number ${resetCounter ? "[counter-reset:numbered-block]" : ""}`}
      style={counterIncrementStyle}
    >
      {/* Circular Counter */}
      <Box className="flex-shrink-0 pt-1">
        <Box className="w-14 h-14 rounded-full border-2 border-secondary flex items-center justify-center text-secondary font-bold text-xl transition-all duration-300 group-hover/number:border-primary group-hover/number:bg-primary group-hover/number:text-primary-foreground shadow-sm before:[content:counter(numbered-block)]"></Box>
      </Box>

      {/* Content */}
      <Box className="space-y-4 pt-2">
        <Heading level={2} className="text-2xl font-bold text-foreground leading-tight">
          {title}
        </Heading>
        <Box className="prose prose-lg dark:prose-invert max-w-none prose-p:leading-relaxed prose-p:text-gray-600">
          <RichText data={content} />
        </Box>
      </Box>
    </Box>
  );
};

export default PortableNumberedBlock;
